const INDEPENDENT_VOWELS: Record<string, string> = {
  a: 'அ',
  aa: 'ஆ',
  i: 'இ',
  ii: 'ஈ',
  ee: 'ஈ',
  u: 'உ',
  uu: 'ஊ',
  oo: 'ஊ',
  e: 'எ',
  ea: 'ஏ',
  ei: 'ஏ',
  ai: 'ஐ',
  o: 'ஒ',
  oa: 'ஓ',
  oi: 'ஓ',
  ou: 'ஔ',
  ow: 'ஔ',
  au: 'ஔ',
}

const DEPENDENT_VOWELS: Record<string, string> = {
  a: '',
  aa: 'ா',
  i: 'ி',
  ii: 'ீ',
  ee: 'ீ',
  u: 'ு',
  uu: 'ூ',
  oo: 'ூ',
  e: 'ெ',
  ea: 'ே',
  ei: 'ே',
  ai: 'ை',
  o: 'ொ',
  oa: 'ோ',
  oi: 'ோ',
  ou: 'ௌ',
  ow: 'ௌ',
  au: 'ௌ',
}

const SPECIAL_WORDS: Record<string, string> = {
  tamil: 'தமிழ்',
  tamizh: 'தமிழ்',
  vanakkam: 'வணக்கம்',
  vanakam: 'வணக்கம்',
  nandri: 'நன்றி',
  amma: 'அம்மா',
  appa: 'அப்பா',
  anna: 'அண்ணா',
  akka: 'அக்கா',
  thambi: 'தம்பி',
  marundhu: 'மருந்து',
  baal: 'பால்',
  paal: 'பால்',
  maal: 'மால்',
  kaal: 'கால்',
  gowtham: 'கௌதம்',
  goutham: 'கௌதம்',
  gautham: 'கௌதம்',
  gowri: 'கௌரி',
  gouri: 'கௌரி',
  shankar: 'சங்கர்',
}

const CLUSTERS: Array<[string, string]> = [
  ['ng', 'ங் க'.replace(' ', '')],
  ['nj', 'ஞ் ச'.replace(' ', '')],
  ['mp', 'ம் ப'.replace(' ', '')],
  ['mb', 'ம் ப'.replace(' ', '')],
  ['ndh', 'ந் த'.replace(' ', '')],
  ['nth', 'ந் த'.replace(' ', '')],
  ['nd', 'ண் ட'.replace(' ', '')],
  ['nt', 'ண் ட'.replace(' ', '')],
]

const CONSONANTS: Array<[string, string]> = [
  ['zh', 'ழ'],
  ['ng', 'ங'],
  ['nj', 'ஞ'],
  ['th', 'த'],
  ['dh', 'த'],
  ['sh', 'ஷ'],
  ['ch', 'ச'],
  ['jh', 'ஜ'],
  ['ph', 'ப'],
  ['bh', 'ப'],
  ['kh', 'க'],
  ['gh', 'க'],
  ['rr', 'ற'],
  ['nn', 'ண'],
  ['ll', 'ள'],
  ['ny', 'ஞ'],
  ['k', 'க'],
  ['g', 'க'],
  ['c', 'க'],
  ['s', 'ச'],
  ['j', 'ஜ'],
  ['t', 'த'],
  ['d', 'ட'],
  ['n', 'ந'],
  ['p', 'ப'],
  ['b', 'ப'],
  ['m', 'ம'],
  ['y', 'ய'],
  ['r', 'ர'],
  ['l', 'ல'],
  ['v', 'வ'],
  ['w', 'வ'],
  ['h', 'ஹ'],
  ['f', 'ஃப'],
]

const VOWEL_KEYS = Object.keys(INDEPENDENT_VOWELS).sort((a, b) => b.length - a.length)
const CLUSTER_KEYS = CLUSTERS.map(([key]) => key).sort((a, b) => b.length - a.length)
const CONSONANT_KEYS = CONSONANTS.map(([key]) => key).sort((a, b) => b.length - a.length)
const CLUSTER_MAP = Object.fromEntries(CLUSTERS)
const CONSONANT_MAP = Object.fromEntries(CONSONANTS)

const CODE_FIELD_PATTERN =
  /(gst|gstin|hsn|sac|ifsc|upi|email|e-mail|mail|phone|mobile|barcode|sku|code|url|website|password|invoice|bill\s*no|qty|quantity|rate|mrp|price|amount|stock|pin|pincode|pan|tan|account|bank|vehicle|date|time|otp|id)/i

const matchFromList = (text: string, index: number, keys: string[]) => {
  for (const key of keys) {
    if (text.startsWith(key, index)) return key
  }
  return ''
}

const applyVowel = (base: string, vowel: string) => `${base}${DEPENDENT_VOWELS[vowel]}`

const transliterateRomanWord = (word: string) => {
  const lower = word.toLowerCase()
  if (SPECIAL_WORDS[lower]) return SPECIAL_WORDS[lower]

  let index = 0
  let output = ''

  while (index < lower.length) {
    const vowel = matchFromList(lower, index, VOWEL_KEYS)
    if (vowel) {
      output += INDEPENDENT_VOWELS[vowel]
      index += vowel.length
      continue
    }

    const cluster = matchFromList(lower, index, CLUSTER_KEYS)
    if (cluster) {
      const nextVowel = matchFromList(lower, index + cluster.length, VOWEL_KEYS)
      if (nextVowel) {
        output += applyVowel(CLUSTER_MAP[cluster], nextVowel)
        index += cluster.length + nextVowel.length
      } else {
        output += `${CLUSTER_MAP[cluster]}்`
        index += cluster.length
      }
      continue
    }

    const consonant = matchFromList(lower, index, CONSONANT_KEYS)
    if (!consonant) {
      output += word[index]
      index += 1
      continue
    }

    const nextVowel = matchFromList(lower, index + consonant.length, VOWEL_KEYS)
    if (nextVowel) {
      output += applyVowel(CONSONANT_MAP[consonant], nextVowel)
      index += consonant.length + nextVowel.length
      continue
    }

    const isWordFinalN = consonant === 'n' && index + consonant.length >= lower.length
    const isWordFinalR = consonant === 'r' && index + consonant.length >= lower.length
    const isWordFinalL = consonant === 'l' && index + consonant.length >= lower.length

    if (isWordFinalN) {
      output += 'ன்'
    } else if (isWordFinalR) {
      output += 'ர்'
    } else if (isWordFinalL) {
      output += 'ல்'
    } else {
      output += `${CONSONANT_MAP[consonant]}்`
    }
    index += consonant.length
  }

  return output
}

export const transliterateTamilText = (value: string) =>
  value.replace(/[A-Za-z]+/g, (match) => transliterateRomanWord(match))

export const shouldSkipTamilTransliteration = (element: HTMLInputElement | HTMLTextAreaElement) => {
  const inputType = element instanceof HTMLInputElement ? element.type : 'textarea'
  const safeTypes = new Set([
    'password',
    'email',
    'number',
    'date',
    'datetime-local',
    'month',
    'time',
    'week',
    'file',
    'hidden',
    'checkbox',
    'radio',
    'range',
    'color',
    'tel',
    'url',
  ])

  if (safeTypes.has(inputType)) return true
  if (element.dataset.noTamilTransliterate === 'true') return true
  if (element.readOnly || element.disabled) return true

  const descriptor = [
    element.name,
    element.id,
    element.placeholder,
    element.getAttribute('aria-label'),
  ]
    .filter(Boolean)
    .join(' ')

  return CODE_FIELD_PATTERN.test(descriptor)
}
